import { Component, ChangeDetectionStrategy, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { Router, RouterLink, ActivatedRoute } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { SubscriptionService, SubscriptionPlan } from '../../services/subscription.service';
import { CountriesService } from '../../services/countries.service';
import { FooterComponent } from '../footer/footer.component';
import { AuthHeaderComponent } from '../auth-header/auth-header.component';

@Component({
  selector: 'app-register',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink, FooterComponent, AuthHeaderComponent],
  template: `
    <main>
      <app-auth-header></app-auth-header>

      <div class="register-scroll">
      <div class="register-container">
      <h1 class="form-title">Registrieren</h1>
      
      <form [formGroup]="form" (ngSubmit)="onSubmit()">
        <div class="form-group">
          <label for="email">E-Mail:</label>
          <input
            id="email"
            type="email"
            formControlName="email"
            placeholder="E-Mail-Adresse"
          />
          @if (form.get('email')?.hasError('required') && form.get('email')?.touched) {
            <span class="error">E-Mail ist erforderlich</span>
          }
          @if (form.get('email')?.hasError('email') && form.get('email')?.touched) {
            <span class="error">Ungültige E-Mail</span>
          }
        </div>

        <div class="form-group">
          <label for="pwd">Passwort:</label>
          <div class="password-wrapper">
            <input
              id="pwd"
              [type]="showPwd() ? 'text' : 'password'"
              formControlName="pwd"
              placeholder="Passwort (mind. 6 Zeichen)"
            />
            <button
              type="button"
              class="toggle-password-btn"
              (click)="showPwd.set(!showPwd())"
              tabindex="-1"
              [attr.aria-label]="showPwd() ? 'Passwort verbergen' : 'Passwort anzeigen'"
            >
              {{ showPwd() ? '👁️‍🗨️' : '👁️' }}
            </button>
          </div>
          @if (form.get('pwd')?.hasError('required') && form.get('pwd')?.touched) {
            <span class="error">Passwort ist erforderlich</span>
          }
          @if (form.get('pwd')?.hasError('minlength') && form.get('pwd')?.touched) {
            <span class="error">Passwort muss mind. 6 Zeichen lang sein</span>
          }
        </div>

        <div class="form-group">
          <label for="pwdConfirm">Passwort wiederholen:</label>
          <div class="password-wrapper">
            <input
              id="pwdConfirm"
              [type]="showPwdConfirm() ? 'text' : 'password'"
              formControlName="pwdConfirm"
              placeholder="Passwort wiederholen"
            />
            <button
              type="button"
              class="toggle-password-btn"
              (click)="showPwdConfirm.set(!showPwdConfirm())"
              tabindex="-1"
              [attr.aria-label]="showPwdConfirm() ? 'Passwort verbergen' : 'Passwort anzeigen'"
            >
              {{ showPwdConfirm() ? '👁️‍🗨️' : '👁️' }}
            </button>
          </div>
          @if (form.get('pwdConfirm')?.hasError('required') && form.get('pwdConfirm')?.touched) {
            <span class="error">Passwort wiederholen ist erforderlich</span>
          }
          @if (form.get('pwdConfirm')?.hasError('minlength') && form.get('pwdConfirm')?.touched) {
            <span class="error">Passwort muss mind. 6 Zeichen lang sein</span>
          }
          @if (!passwordsMatch() && (form.get('pwd')?.touched || form.get('pwdConfirm')?.touched)) {
            <span class="error">Passwörter stimmen nicht überein</span>
          }
        </div>

        <div class="form-group">
          <label for="plan">Abonnement wählen:</label>
          <select id="plan" formControlName="selectedPlan">
            @for (plan of availablePlans(); track plan.planKey) {
              <option [value]="plan.planKey">
                {{ plan.name }} - {{ plan.priceEUR === 0 ? 'Kostenlos' : (plan.priceEUR | number:'1.2-2') + '€' }}
                @if (plan.discountPercent && plan.discountPercent > 0) {
                  ({{ plan.discountPercent }}% Rabatt)
                }
              </option>
            }
          </select>
        </div>

        <div class="form-group checkbox-group">
          <div class="checkbox-label">
            <input
              id="isBusinessUse"
              type="checkbox"
              formControlName="isBusinessUse"
              class="checkbox-input"
            />
            <label for="isBusinessUse" class="checkbox-text">Geschäftliche Nutzung</label>
            <button
              type="button"
              class="info-btn"
              (click)="showTooltip('b2b', $event)"
              aria-label="Hilfe zu Geschäftliche Nutzung"
              tabindex="0"
            >
              ℹ
            </button>
          </div>
        </div>

        <div class="form-group">
          <label for="country">Land:</label>
          <select id="country" formControlName="selectedCountry">
            <option value="" disabled selected>-- Land wählen --</option>
            @for (country of countries(); track country) {
              <option [value]="country">{{ country }}</option>
            }
          </select>
          <span class="plan-info">
            Unsere Dienstleistungen beziehen sich ausschließlich auf Immobilien in Deutschland.
          </span>
          @if (form.get('selectedCountry')?.hasError('required') && form.get('selectedCountry')?.touched) {
            <span class="error">Land ist erforderlich</span>
          }
        </div>

        @if (authService.errorMessage()) {
          <div class="error-message">
            {{ authService.errorMessage() }}
          </div>
        }

        @if (registrationSuccess()) {
          <div class="success-message">
            Registrierung erfolgreich! Sie werden weitergeleitet zur E-Mail-Verifizierung...
          </div>
        }

        @if (!registrationSuccess()) {
          <button type="submit" [disabled]="!form.valid || isLoading() || !passwordsMatch()">
            @if (isLoading()) {
              Wird registriert...
            } @else {
              Registrieren
            }
          </button>
        }
      </form>

      <p class="login-link">
        Bereits registriert?
        <a routerLink="/login">Hier anmelden</a>
      </p>
      </div>
      </div>
    </main>

    <!-- Tooltip Overlay -->
    @if (isTooltipActive('b2b')) {
      <div class="tooltip-overlay active" (click)="hideTooltip()"></div>
      <div class="tooltip-content">
        <div class="tooltip-header">
          <h2 class="tooltip-title">Geschäftliche Nutzung</h2>
          <button type="button" class="close-btn" (click)="hideTooltip()" aria-label="Schließen">×</button>
        </div>
        <div class="tooltip-body">
          <p>Diese Information ist für die Rechnungsstellung wichtig!</p>
        </div>
      </div>
    }

    <app-footer></app-footer>
  `,
  styles: [`
    :host {
      font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
        Helvetica, Arial, sans-serif;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      height: 100vh;
      overflow: hidden;
    }

    main {
      flex: 1;
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 0;
      padding: 0;
      min-height: 0;
      box-sizing: border-box;
    }

    .register-scroll {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      padding: 22px 2rem 1.5rem;
      box-sizing: border-box;
    }

    main > div:not(.header-container) {
      padding: 0 2rem;
    }

    main > div:first-child {
      padding: 0;
    }

    .register-container {
      max-width: 525px !important;
      margin: auto;
      padding: 0 40px;
      border: 1px solid #ccc;
      border-radius: 8px;
      background: #f9f9f9;
      width: 100%;
      box-sizing: border-box;
    }

    h1 {
      text-align: center;
      margin-bottom: 10px;
      color: #333;
    }

    .form-group {
      margin-bottom: 0;
      display: flex;
      flex-direction: column;
    }

    label {
      margin-bottom: 5px;
      color: #555;
      font-weight: 500;
    }

    input {
      padding: 10px;
      border: 1px solid #ddd;
      border-radius: 4px;
      font-size: 14px;
      font-family: inherit;
    }

    input:focus {
      outline: none;
      border-color: #4CAF50;
      box-shadow: 0 0 5px rgba(76, 175, 80, 0.3);
    }

    select {
      padding: 10px;
      border: 1px solid #ddd;
      border-radius: 4px;
      font-size: 14px;
      font-family: inherit;
      background-color: white;
      cursor: pointer;
    }

    select:focus {
      outline: none;
      border-color: #4CAF50;
      box-shadow: 0 0 5px rgba(76, 175, 80, 0.3);
    }

    .plan-info {
      font-size: 12px;
      color: #666;
      margin-top: 5px;
      font-style: italic;
    }

    .password-wrapper {
      position: relative;
      display: flex;
      align-items: center;
    }

    .password-wrapper input {
      width: 100%;
      padding-right: 35px;
    }

    /* Browser-native Password Reveal Button verstecken */
    .password-wrapper input::-webkit-credentials-auto-fill-button,
    .password-wrapper input::-webkit-outer-spin-button,
    .password-wrapper input::-webkit-inner-spin-button {
      display: none;
    }

    .password-wrapper input::-ms-reveal {
      display: none;
    }

    .toggle-password-btn {
      position: absolute;
      right: 8px;
      top: 50%;
      transform: translateY(-50%);
      background: none;
      border: none;
      cursor: pointer;
      font-size: 16px;
      padding: 0;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #666;
    }

    .toggle-password-btn:hover {
      background-color: white;
      border-radius: 4px;
    }

    .toggle-password-btn:focus {
      outline: none;
    }

    .checkbox-group {
      margin-bottom: 20px;
      display: flex;
      flex-direction: column;
    }

    .checkbox-label {
      display: flex;
      align-items: center;
      gap: 8px;
      font-weight: 500;
      color: #555;
      cursor: default;
      margin-top: 4px;
    }

    .checkbox-input {
      width: 18px;
      height: 18px;
      cursor: pointer;
      margin: 0;
      accent-color: auto;
    }

    .checkbox-text {
      white-space: nowrap;
      cursor: pointer;
    }

    .info-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: oklch(51.01% 0.274 263.83);
      color: white;
      border: none;
      border-radius: 50%;
      width: 20px;
      height: 20px;
      font-size: 12px;
      font-weight: bold;
      cursor: pointer;
      margin-left: -4px;
      padding: 0;
      line-height: 20px;
      text-align: center;
      flex-shrink: 0;
      transition: all 0.2s ease;
    }

    .info-btn:hover {
      background: #2563eb;
      transform: scale(1.1);
    }

    .info-btn:active {
      transform: scale(0.95);
    }

    .tooltip-overlay {
      display: none;
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      z-index: 9998;
      animation: fadeIn 0.2s ease;
    }

    .tooltip-overlay.active {
      display: block;
    }

    .tooltip-content {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      background: white;
      border-radius: 16px 16px 0 0;
      padding: 1.5rem;
      max-height: 70vh;
      overflow-y: auto;
      z-index: 9999;
      box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.15);
      animation: slideUp 0.3s ease;
    }

    .tooltip-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 1rem;
      padding-bottom: 0.75rem;
      border-bottom: 2px solid #e5e7eb;
    }

    .tooltip-title {
      font-weight: 600;
      color: oklch(51.01% 0.274 263.83);
      font-size: 1.1rem;
      margin: 0;
    }

    .close-btn {
      background: #f3f4f6;
      border: none;
      border-radius: 50%;
      width: 32px;
      height: 32px;
      font-size: 20px;
      font-weight: bold;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #6b7280;
      transition: all 0.2s ease;
      padding: 0;
      flex-shrink: 0;
    }

    .close-btn:hover {
      background: #e5e7eb;
      color: #374151;
    }

    .close-btn:active {
      transform: scale(0.95);
    }

    .tooltip-body {
      font-size: 0.95rem;
      line-height: 1.6;
      color: #333;
    }

    .tooltip-body p {
      margin: 0;
    }

    @keyframes fadeIn {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }

    @keyframes slideUp {
      from {
        transform: translateY(100%);
      }
      to {
        transform: translateY(0);
      }
    }

    .error {
      color: #d32f2f;
      font-size: 12px;
      margin-top: 5px;
    }

    .error-message {
      background-color: #ffebee;
      color: #d32f2f;
      padding: 10px;
      border-radius: 4px;
      margin-bottom: 15px;
      border-left: 4px solid #d32f2f;
    }

    .success-message {
      background-color: #e8f5e9;
      color: #2e7d32;
      padding: 10px;
      border-radius: 4px;
      margin-bottom: 15px;
      border-left: 4px solid #2e7d32;
    }

    button {
      width: 100%;
      padding: 10px;
      background-color: #4CAF50;
      color: white;
      border: none;
      border-radius: 4px;
      font-size: 16px;
      font-weight: 500;
      cursor: pointer;
      transition: background-color 0.3s;
    }

    button[type="submit"] {
      margin-top: 3px;
    }

    button:not(.toggle-password-btn):not(.info-btn):not(.close-btn):hover:not(:disabled) {
      background-color: #45a049;
    }

    button:disabled {
      background-color: #cccccc;
      cursor: not-allowed;
    }

    .login-link {
      text-align: center;
      margin-top: 20px;
      font-size: 14px;
    }

    .login-link a {
      color: #4CAF50;
      text-decoration: none;
      font-weight: 500;
    }

    .login-link a:hover {
      text-decoration: underline;
    }

    @media (max-width: 480px) {
      /* Reduce all text inside register box by 1pt (~1.33px) */
      .register-container { font-size: 14.67px; }
      .register-container h1 { font-size: 22.67px; }
      .register-container label { font-size: 14.67px; }
      .register-container input { font-size: 12.67px; }
      .register-container select { font-size: 12.67px; }
      .register-container .plan-info { font-size: 10.67px; }
      .register-container .toggle-password-btn { font-size: 14.67px; }
      .register-container .info-btn { font-size: 10.67px; }
      .register-container .tooltip-title { font-size: 1.017rem; }
      .register-container .close-btn { font-size: 18.67px; }
      .register-container .tooltip-body { font-size: 0.867rem; }
      .register-container .error { font-size: 10.67px; }
      .register-container button { font-size: 14.67px; }
      .register-container .login-link { font-size: 12.67px; }
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RegisterComponent {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  protected authService = inject(AuthService);
  private subscriptionService = inject(SubscriptionService);
  private countriesService = inject(CountriesService);

  registrationSuccess = signal(false);
  isLoading = signal(false);
  passwordsMatch = signal(true);
  showPwd = signal(false);
  showPwdConfirm = signal(false);
  availablePlans = signal<SubscriptionPlan[]>([]);
  countries = signal<string[]>([]);
  activeTooltip = signal<string | null>(null);
  
  selectedPlanDetails = computed(() => {
    const planKey = this.form.get('selectedPlan')?.value;
    return this.availablePlans().find(p => p.planKey === planKey);
  });

  form = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    pwd: ['', [Validators.required, Validators.minLength(6)]],
    pwdConfirm: ['', [Validators.required, Validators.minLength(6)]],
    selectedPlan: ['trial', [Validators.required]],
    isBusinessUse: [false],
    selectedCountry: ['', [Validators.required]]
  });

  constructor() {
    // Fehlermeldung von vorherigen Versuchen löschen
    this.authService.errorMessage.set(null);
    
    // Überwache Änderungen in beiden Passwort-Feldern
    this.form.get('pwd')?.valueChanges.subscribe(() => this.checkPasswords());
    this.form.get('pwdConfirm')?.valueChanges.subscribe(() => this.checkPasswords());
    
    // Lade verfügbare Pläne
    this.loadPlans();
    
    // Lade Länder
    this.loadCountries();
    
    // Lese Plan aus QueryParams
    this.route.queryParams.subscribe(params => {
      if (params['plan']) {
        this.form.patchValue({ selectedPlan: params['plan'] });
      }
    });
  }
  
  private loadCountries() {
    this.countriesService.getCountries().subscribe({
      next: (countries: string[]) => {
        this.countries.set(countries);
        // Setze Deutschland als Default
        if (countries.includes('Deutschland')) {
          this.form.patchValue({ selectedCountry: 'Deutschland' });
        }
      },
      error: (error) => {
        console.error('Fehler beim Laden der Länder:', error);
      }
    });
  }
  
  private loadPlans() {
    this.subscriptionService.getAvailablePlans().subscribe({
      next: (plans) => {
        this.availablePlans.set(plans);
      },
      error: (error) => {
        console.error('Fehler beim Laden der Pläne:', error);
      }
    });
  }

  private checkPasswords() {
    const pwd = this.form.get('pwd')?.value || '';
    const pwdConfirm = this.form.get('pwdConfirm')?.value || '';
    this.passwordsMatch.set(pwd === pwdConfirm);
  }

  showTooltip(fieldId: string, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.activeTooltip.set(fieldId);
  }

  hideTooltip() {
    this.activeTooltip.set(null);
  }

  isTooltipActive(fieldId: string): boolean {
    return this.activeTooltip() === fieldId;
  }

  onSubmit() {
    if (this.form.valid) {
      const { email, pwd, pwdConfirm, selectedPlan, isBusinessUse, selectedCountry } = this.form.value;
      
      // Zusätzliche Validierung: Passwörter müssen übereinstimmen
      if (pwd !== pwdConfirm) {
        return;
      }
      
      if (email && pwd && selectedCountry) {
        this.isLoading.set(true);
        this.authService.register(email, pwd, selectedCountry, isBusinessUse || false).subscribe({
          next: () => {
            this.isLoading.set(false);
            this.registrationSuccess.set(true);
            
            // Store selected plan and country in localStorage for post-verification flow
            if (selectedPlan) {
              localStorage.setItem('selectedPlan', selectedPlan);
            }
            if (selectedCountry) {
              localStorage.setItem('selectedCountry', selectedCountry);
            }
            
            setTimeout(() => {
              this.router.navigate(['/verify-email'], { 
                queryParams: { email } 
              });
            }, 2000);
          },
          error: (err) => {
            this.isLoading.set(false);
          }
        });
      }
    }
  }
}
